import { APP_ACP } from "core/consts";
import { ENV_PRODUCTION } from "core/model/config";
import { pathToUrl } from "core/model/utils/urls";
import {
  buildAppUrl,
  getAppRoutePrefix,
} from "core/model/utils/urls/getBaseUrl";
import { Location, NavigateFunction } from "react-router-dom";

const dashboardRoute = "/acp/dashboard";

export type AcpUrls = ReturnType<typeof getAcpHandlerUrls>;

export const getAcpHandlerUrls = ({ location }: { location: Location }) => {
  const { search } = location;

  return {
    goToAccount(id: number) {
      return pathToUrl({
        pathname: `${dashboardRoute}/account/${id}`,
        search,
      });
    },
    goToAccountSlug(id: number, accountSlug: string) {
      return pathToUrl({
        pathname: `${dashboardRoute}/account/${id}/${accountSlug}`,
        search,
      });
    },
    goToAi() {
      return pathToUrl({
        pathname: `${dashboardRoute}/ai`,
        search,
      });
    },
    goToAiWorkbench() {
      return pathToUrl({
        pathname: `${dashboardRoute}/ai-workbench`,
        search,
      });
    },
    goToCareproviderPage(id: number, page: string) {
      return pathToUrl({
        pathname: `${dashboardRoute}/careprovider/${id}/${page}`,
        search,
      });
    },
    goToCareseeker(id: number) {
      return pathToUrl({
        pathname: `${dashboardRoute}/careseeker/${id}`,
        search,
      });
    },
    goToCareseekerSlug(id: number, careseekerSlug: string) {
      return pathToUrl({
        pathname: `${dashboardRoute}/careseeker/${id}/${careseekerSlug}`,
        search,
      });
    },
    goToCareseekerSSO(id: number) {
      return pathToUrl({
        pathname: `${dashboardRoute}/careseeker/${id}/sso`,
        search,
      });
    },
    goToCompany(id: number) {
      return pathToUrl({
        pathname: `${dashboardRoute}/company/${id}`,
        search,
      });
    },
    goToConsultant(id: number) {
      return pathToUrl({
        pathname: `${dashboardRoute}/consultant/${id}`,
        search,
      });
    },
    goToCreateCareprovider() {
      return pathToUrl({
        pathname: `${dashboardRoute}/create/careprovider`,
        search,
      });
    },
    goToCreateCareseeker() {
      return pathToUrl({
        pathname: `${dashboardRoute}/create/careseeker`,
        search,
      });
    },
    goToCreateConsultant() {
      return pathToUrl({
        pathname: `${dashboardRoute}/create/consultant`,
        search,
      });
    },
    goToDashboard() {
      return pathToUrl({
        pathname: `${dashboardRoute}`,
        search,
      });
    },
    goToDebugPage() {
      return pathToUrl({
        pathname: `${dashboardRoute}/info`,
        search,
      });
    },
    goToEmailTranslations() {
      return pathToUrl({
        pathname: `${dashboardRoute}/emailtranslations`,
        search,
      });
    },
    goToForgotPassword() {
      return pathToUrl({
        pathname: "/acp/forgot-password",
        search,
      });
    },
    goToInsurance(id: number | null) {
      return pathToUrl({
        pathname: id
          ? `${dashboardRoute}/insurance/${id}`
          : `${dashboardRoute}/insurance`,
        search,
      });
    },
    goToInternalAccounts() {
      return pathToUrl({
        pathname: `${dashboardRoute}/staff`,
        search,
      });
    },
    goToLogin() {
      return pathToUrl({
        pathname: "/acp/auth",
        search,
      });
    },
    goToManuallyAddedProviders() {
      return pathToUrl({
        pathname: `${dashboardRoute}/manuallyadded`,
        search,
      });
    },
    goToNativeTranslations() {
      return pathToUrl({
        pathname: `${dashboardRoute}/nativetranslations`,
        search,
      });
    },
    goToPlayground() {
      return pathToUrl({
        pathname: `${dashboardRoute}/playground`,
        search,
      });
    },
    goToProductionEnv() {
      return buildAppUrl({
        app: APP_ACP,
        env: ENV_PRODUCTION,
        path: getAppRoutePrefix(APP_ACP),
      });
    },
    goToRecareCdr() {
      return pathToUrl({
        pathname: `${dashboardRoute}/demo-cdr`,
        search,
      });
    },
    goToSealdDebug(page?: string) {
      return pathToUrl({
        pathname: page
          ? `${dashboardRoute}/sealddebug/${page}`
          : `${dashboardRoute}/sealddebug/account`,
        search: "",
      });
    },
    goToSearch() {
      return pathToUrl({
        pathname: `${dashboardRoute}/search`,
        search,
      });
    },
    goToSendTestEmail() {
      return pathToUrl({
        pathname: `${dashboardRoute}/testemail`,
        search,
      });
    },
    goToTranslations() {
      return pathToUrl({
        pathname: `${dashboardRoute}/translations`,
        search,
      });
    },
    goToVoiceTool() {
      return pathToUrl({
        pathname: `${dashboardRoute}/voicetool`,
        search,
      });
    },
  };
};

type AcpNavigation = {
  [K in keyof AcpUrls]: (...args: Parameters<AcpUrls[K]>) => void;
};

export const getAcpNavigationHandlers = ({
  navigate,
  urls,
}: {
  navigate: NavigateFunction;
  urls: AcpUrls;
}): AcpNavigation => ({
  goToAccount(id: number) {
    navigate(urls.goToAccount(id));
    window.scrollTo(0, 0);
  },
  goToAccountSlug(id: number, accountSlug: string) {
    navigate(urls.goToAccountSlug(id, accountSlug));
  },
  goToAi() {
    navigate(urls.goToAi());
  },
  goToAiWorkbench() {
    navigate(urls.goToAiWorkbench());
  },
  goToCareproviderPage(id: number, page: string) {
    navigate(urls.goToCareproviderPage(id, page));
  },
  goToCareseeker(id: number) {
    navigate(urls.goToCareseeker(id));
  },
  goToCareseekerSlug(id: number, careseekerSlug: string) {
    navigate(urls.goToCareseekerSlug(id, careseekerSlug));
  },
  goToCareseekerSSO(id: number) {
    navigate(urls.goToCareseekerSSO(id));
  },
  goToCompany(id: number) {
    navigate(urls.goToCompany(id));
  },
  goToConsultant(id: number) {
    navigate(urls.goToConsultant(id));
  },
  goToCreateCareprovider() {
    navigate(urls.goToCreateCareprovider());
  },
  goToCreateCareseeker() {
    navigate(urls.goToCreateCareseeker());
  },
  goToCreateConsultant() {
    navigate(urls.goToCreateConsultant());
  },
  goToDashboard() {
    navigate(urls.goToDashboard());
  },
  goToDebugPage() {
    navigate(urls.goToDebugPage());
  },
  goToEmailTranslations() {
    navigate(urls.goToEmailTranslations());
  },
  goToForgotPassword() {
    navigate(urls.goToForgotPassword());
  },
  goToInsurance(id: number | null) {
    navigate(urls.goToInsurance(id));
  },
  goToInternalAccounts() {
    navigate(urls.goToInternalAccounts());
  },
  goToLogin() {
    navigate(urls.goToLogin());
  },
  goToManuallyAddedProviders() {
    navigate(urls.goToManuallyAddedProviders());
  },
  goToNativeTranslations() {
    navigate(urls.goToNativeTranslations());
  },
  goToPlayground() {
    navigate(urls.goToPlayground());
  },
  goToProductionEnv() {
    navigate(urls.goToForgotPassword());
  },
  goToRecareCdr() {
    navigate(urls.goToRecareCdr());
  },
  goToSealdDebug(page?: string) {
    navigate(urls.goToSealdDebug(page));
  },
  goToSearch() {
    navigate(urls.goToSearch());
  },
  goToSendTestEmail() {
    navigate(urls.goToSendTestEmail());
  },
  goToTranslations() {
    navigate(urls.goToTranslations());
  },
  goToVoiceTool() {
    navigate(urls.goToVoiceTool());
  },
});
